import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { STORAGE_KEYS } from '@/shared/constants'
import { isTokenValid, isAuthTokenValid, setAuthTokens, clearAuthStorage } from '@/shared/services/http'
import { authApi } from '@/features/auth/api/auth.api'
import { LoginCredentials } from '@/features/auth/types'

interface ProtectedRouteProps {
    children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
    const location = useLocation()

    // Check authentication synchronously on mount to prevent showing loader when token is valid
    const checkAuthSync = () => {
        try {
            const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
            return !!(accessToken && isTokenValid(accessToken) && isAuthTokenValid())
        } catch {
            return false
        }
    }

    const isInitiallyAuth = checkAuthSync()
    const [authenticated, setAuthenticated] = useState(isInitiallyAuth)

    const logout = (clearCredentials = false) => {
        const shouldClear = clearCredentials === true
        clearAuthStorage(shouldClear)
        setAuthenticated(false)
        window.location.href = location.pathname.startsWith('/akashic-unstructured-data')
            ? '/akashic-unstructured-data/login'
            : '/'
    }

    const refreshByCredentials = async () => {
        const credsRaw = localStorage.getItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
        if (!credsRaw) {
            logout(false)
            return
        }

        const credentials = JSON.parse(credsRaw) as LoginCredentials
        try {
            const loginResult = await authApi.login(credentials)

            const accessToken = loginResult.data.tokens.access_token
            const refreshToken = (loginResult.data.tokens as any).refresh_token
            const expiresIn = loginResult.data.tokens.expires_in
            const refreshExpiresIn = loginResult.data.tokens.refresh_expires_in

            setAuthTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn)
            setAuthenticated(true)
        } catch (error: any) {
            const isActualAuthError = error?.response?.status === 401 || error?.response?.status === 400
            logout(isActualAuthError)
        }
    }

    const scheduleWarning = () => {
        // Disabled auto-logout/warning as per user request to remain logged in until manual logout
        return;
    }

    useEffect(() => {
        const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)

        if (accessToken && isTokenValid(accessToken) && isAuthTokenValid()) {
            setAuthenticated(true)
            scheduleWarning()
            return
        }

        const doProactiveRefresh = async () => {
            const rToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
            if (rToken) {
                try {
                    const refreshResult = await authApi.refreshToken(rToken)
                    const tokens = (refreshResult?.data?.tokens ?? refreshResult?.data ?? refreshResult) as any
                    const newAccessToken = tokens?.access_token ?? tokens?.accessToken
                    if (newAccessToken) {
                        const newRefreshToken = tokens?.refresh_token ?? tokens?.refreshToken ?? rToken
                        const newExpiresIn = tokens?.expires_in ?? tokens?.expiresIn ?? 3600
                        const newRefreshExpiresIn = tokens?.refresh_expires_in ?? tokens?.refreshExpiresIn

                        setAuthTokens(newAccessToken, newExpiresIn, newRefreshToken, newRefreshExpiresIn)
                        setAuthenticated(true)
                        scheduleWarning()
                        return
                    }
                } catch (err) {
                    console.error("Proactive token refresh failed on mount", err)
                }
            }

            // Fallback to credentials
            const credsRaw = localStorage.getItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
            if (credsRaw) {
                try {
                    await refreshByCredentials()
                    return
                } catch (err: any) {
                    console.error("Proactive credentials login failed on mount", err)
                    const isActualAuthError = err?.response?.status === 401 || err?.response?.status === 400
                    logout(isActualAuthError)
                    return
                }
            }

            logout(false)
        }

        const hasRefreshToken = !!localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
        const hasCredentials = !!localStorage.getItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
        if (hasRefreshToken || hasCredentials) {
            doProactiveRefresh()
        } else {
            setAuthenticated(false)
        }

    }, [])

    if (!authenticated) {
        const loginRedirectPath = location.pathname.startsWith('/akashic-unstructured-data')
            ? '/akashic-unstructured-data/login'
            : '/'
        return <Navigate to={loginRedirectPath} state={{ from: location }} replace />
    }

    return (
        <>
            {children}
        </>
    )
}


